import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "antd";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import CommonStyles from "@components/CommonStyles";
import { optionEthics, optionGender } from "@base/constants";
import useFormValidation from "@base/hooks/useFormvalidation";
import useToggle from "@base/hooks/useToggle";
import { IProvince, IWard } from "@base/modules/administrative/administrative.interface";
import administrativeService from "@base/modules/administrative/administrativeService";
import { foldVietnamese } from "@base/modules/doctor/doctorProfile";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { RootState } from "@base/store/store";

interface IFormValues {
  patient_name: string;
  patient_gender: boolean;
  patient_dob?: Dayjs;
  patient_ethic: string;
  administrative_province_code?: string;
  administrative_ward_code?: string;
  patient_address: string;
  contact_phone?: string;
  patient_email?: string;
  default_record: boolean;
}

interface IProps {
  open: boolean;
  toggle: () => void;
  /** Names already on the account - creating a second one asks first. */
  existingNames?: string[];
  onCreated: (record: IPatientRecord) => void;
}

/**
 * Adds a patient record from a booking flow (spec "Thêm hồ sơ"): name, gender, date of birth,
 * nationality, province, ward and street are required; the account's phone is the record's own.
 */
export default function PatientRecordForm({ open, toggle, existingNames = [], onCreated }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm<IFormValues>();
  const { patientRecord, accountSetting } = useFormValidation();
  const userInfo = useSelector((state: RootState) => state.appCommonSlice.userInfo);
  const { open: openDuplicate, shouldRender: shouldRenderDuplicate, toggle: toggleDuplicate } = useToggle();
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [wards, setWards] = useState<IWard[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pending, setPending] = useState<IFormValues>();

  //! function
  useEffect(() => {
    administrativeService
      .getAllProvinceSorted()
      .then((resp) => setProvinces(resp.data ?? []))
      .catch(() => undefined);
  }, []);

  const onChangeProvince = async (provinceCode: string) => {
    form.setFieldValue("administrative_ward_code", undefined);
    setWards([]);

    try {
      const resp = await administrativeService.getListWardByProvinceCode(provinceCode);
      setWards(resp.data ?? []);
    } catch (error) {}
  };

  const save = async (values: IFormValues) => {
    const province = provinces.find((item) => item.code === values.administrative_province_code);
    const ward = wards.find((item) => item.code === values.administrative_ward_code);
    const record: IPatientRecord = {
      patient_name: values.patient_name.trim().toUpperCase(),
      patient_gender: values.patient_gender,
      patient_dob: values.patient_dob?.format("YYYY-MM-DD"),
      patient_ethic: values.patient_ethic,
      patient_nationality: "084",
      patient_phone_number: userInfo?.user_name,
      contact_phone: values.contact_phone?.trim() || userInfo?.user_name,
      patient_email: values.patient_email?.trim() || undefined,
      patient_address: values.patient_address.trim(),
      administrative_province_code: province?.code,
      administrative_province_name: province?.name,
      administrative_ward_code: ward?.code,
      administrative_ward_name: ward?.name,
      administrative_district_code: "",
      administrative_district_name: "",
      default_record: values.default_record,
    };

    try {
      setIsSaving(true);
      const resp = await profileService.createPatientRecord({ id: 0, active: true, user_key: userInfo?.user_key, patient_record: record });
      const created = resp.data?.patient_record;

      if (created?.code && values.default_record) {
        await profileService.setDefault(created.code).catch(() => undefined);
      }

      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Successfully") + "!" }));
      onCreated(created ?? record);
    } catch (error) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("Failed") + "!" }));
    } finally {
      setIsSaving(false);
    }
  };

  const onFinish = (values: IFormValues) => {
    const name = foldVietnamese(values.patient_name.trim());

    if (existingNames.some((existing) => foldVietnamese(existing.trim()) === name)) {
      setPending(values);
      toggleDuplicate();
      return;
    }

    save(values);
  };

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      width={800}
      title={t("booking.record.addTitle")}
      content={
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ patient_gender: true, patient_ethic: "25", default_record: false, contact_phone: userInfo?.user_name }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
            <CommonStyles.FormFieldUI
              name="patient_name"
              rules={[...patientRecord.patientName(), { whitespace: true, max: 255, message: t("booking.record.nameInvalid") }]}
              normalize={(value: string) => value?.toUpperCase()}
              component={<CommonStyles.InputUI variant="filled" label={t("user.profile.handler.labelName")} required maxLength={255} />}
            />

            <CommonStyles.FormFieldUI
              name="patient_dob"
              rules={patientRecord.patientDob()}
              component={
                <CommonStyles.DatePickerUI
                  variant="filled"
                  label={t("user.profile.handler.labelDateOfBirth")}
                  required
                  format="DD/MM/YYYY"
                  disabledDate={(current: Dayjs) => !!current && current.isAfter(dayjs(), "day")}
                />
              }
            />

            <CommonStyles.FormFieldUI
              name="patient_gender"
              component={
                <CommonStyles.RadioUI
                  options={optionGender}
                  label={t("user.profile.handler.labelGender")}
                  handleChange={(event) => form.setFieldValue("patient_gender", event.target.value)}
                />
              }
            />

            <CommonStyles.FormFieldUI
              name="patient_ethic"
              component={
                <CommonStyles.RadioUI
                  options={optionEthics}
                  label={t("user.profile.handler.labelEthic")}
                  handleChange={(event) => form.setFieldValue("patient_ethic", event.target.value)}
                />
              }
            />

            <CommonStyles.FormFieldUI
              name="administrative_province_code"
              rules={patientRecord.province()}
              component={
                <CommonStyles.SelectUI
                  variant="filled"
                  showSearch
                  optionFilterProp="label"
                  label={t("user.profile.handler.labelProvince")}
                  required
                  options={provinces.map((province) => ({ label: province.name, value: province.code }))}
                  onChange={onChangeProvince}
                />
              }
            />

            <CommonStyles.FormFieldUI
              name="administrative_ward_code"
              rules={patientRecord.district()}
              component={
                <CommonStyles.SelectUI
                  variant="filled"
                  showSearch
                  optionFilterProp="label"
                  label={t("user.profile.handler.labelDistrict")}
                  required
                  options={wards.map((ward) => ({ label: ward.name, value: ward.code }))}
                />
              }
            />

            <div className="sm:col-span-2">
              <CommonStyles.FormFieldUI
                name="patient_address"
                rules={[...patientRecord.address(), { whitespace: true, message: `${t("user.profile.handler.labelAddress")} ${t("Empty")}` }]}
                component={<CommonStyles.InputUI variant="filled" label={t("user.profile.handler.labelAddress")} required maxLength={255} />}
              />
            </div>

            <CommonStyles.FormFieldUI
              name="contact_phone"
              component={<CommonStyles.InputUI variant="filled" label={t("booking.record.contactPhone")} maxLength={20} />}
            />

            <CommonStyles.FormFieldUI
              name="patient_email"
              rules={accountSetting.email()}
              component={<CommonStyles.InputUI variant="filled" label={t("user.profile.handler.labelEmail")} maxLength={255} />}
            />

            <div className="sm:col-span-2">
              <Form.Item name="default_record" valuePropName="checked">
                <CommonStyles.CheckBoxUI titleCustom={t("booking.record.makeDefault")} />
              </Form.Item>
            </div>

            <div className="sm:col-span-2 flex justify-center items-center">
              <CommonStyles.ButtonUI htmlType="submit" className="h-12 px-6" loading={isSaving}>
                {t("user.profile.handler.btnSave")}
              </CommonStyles.ButtonUI>
            </div>
          </div>

          {shouldRenderDuplicate && (
            <CommonStyles.ModalConfirmationUI
              open={openDuplicate}
              toggle={toggleDuplicate}
              title={t("booking.record.duplicateTitle")}
              content={t("booking.record.duplicateMessage", { name: pending?.patient_name.trim().toUpperCase() })}
              onConfirm={() => {
                toggleDuplicate();

                if (pending) {
                  save(pending);
                }
              }}
            />
          )}
        </Form>
      }
    />
  );
}
